import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Outlet, useHref, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Sidebar, TopBar, useBreakpoint, type Crumb, type SidebarItem } from '@grow/ui';
import { useAuth } from '../auth/AuthContext.js';
import logoInline from '../assets/logo-inline.png';

/** A parent crumb names the route it links to; a plain string is just a label. */
type MetaCrumb = string | { label: string; to: string };

interface ScreenMeta {
  crumbs: MetaCrumb[];
  title: string;
  lead?: string;
  actions?: ScreenAction[];
}

interface ScreenAction {
  label: string;
  variant: 'primary' | 'secondary' | 'tertiary';
  to?: string;
}

const SCREEN_META: Record<string, ScreenMeta> = {
  dashboard: {
    crumbs: ['Workspace', 'Dashboard'],
    // Both of these are rebuilt per-user in the component below: the greeting
    // takes the signed-in name, and a portfolio-scoped User gets a lead that
    // doesn't advertise the manager picker they can't see.
    title: 'Good morning.',
    lead: 'Company-wide results and per-manager performance. Filter by manager and date, then export to CSV for Excel.',
    // Export lives in the dashboard's own filter bar now (it has to carry the
    // active filters), so the top bar only keeps the send action.
    actions: [{ label: 'Send survey', variant: 'primary', to: '/compose' }],
  },
  responses: {
    crumbs: ['Workspace', 'Responses'],
    title: 'All survey responses',
    // `lead` is filled in dynamically from the dashboard's 30-day completed count
    // (see RESPONSES_WINDOW_DAYS / dashboard query below).
  },
  detractors: {
    crumbs: ['Workspace', 'Detractor alerts'],
    title: 'Customers needing attention',
    lead: 'Detractors must be contacted within 24 hours of submission.',
    actions: [{ label: 'Export', variant: 'secondary' }],
  },
  compose: {
    crumbs: ['Workspace', 'Send survey'],
    title: 'Send a new survey',
    lead: 'Choose a survey type and recipients. General CX sends in bulk; the triggered surveys send per PropertyMe event.',
  },
  drip: {
    crumbs: ['Workspace', 'Drip schedule'],
    title: 'Drip schedule',
    lead: 'Preview who the daily drip will contact on each day and fix any odd owner names before they send.',
  },
  contacts: {
    crumbs: ['Workspace', { label: 'Contacts', to: '/contacts' }],
    title: 'Contacts',
    lead: 'Add a contact, then send them the survey via SMS.',
    actions: [{ label: 'Add contact', variant: 'primary', to: '/contacts/new' }],
  },
  properties: {
    crumbs: ['Workspace', { label: 'Properties', to: '/properties' }],
    title: 'Properties',
    lead: 'Each property with its owners and manager — send the survey to all owners at once.',
  },
  'contacts-new': {
    crumbs: ['Workspace', { label: 'Contacts', to: '/contacts' }, 'Add contact'],
    title: 'Add a contact',
    lead: 'Capture the customer and their property details.',
  },
  'contacts-edit': {
    crumbs: ['Workspace', { label: 'Contacts', to: '/contacts' }, 'Edit contact'],
    title: 'Edit contact',
    lead: 'Update the customer and their property details.',
  },
  detail: {
    crumbs: ['Workspace', { label: 'Responses', to: '/responses' }, 'Response detail'],
    title: 'Response detail',
  },
  surveys: {
    crumbs: ['Workspace', 'Surveys'],
    title: 'Surveys',
    lead: 'Duplicate a survey to run a one-off campaign — the copy is separate, so editing it never changes the original.',
  },
  'survey-questions': {
    // The last crumb becomes the survey's name once its questions load (below).
    crumbs: ['Workspace', { label: 'Surveys', to: '/surveys' }, 'Questions'],
    title: 'Survey questions',
    lead: 'Edit this survey only. Every other survey is unaffected.',
  },
  portfolios: {
    crumbs: ['Workspace', 'Portfolios'],
    title: 'Portfolios & managers',
    lead: 'The manager shown here is the name inserted into each survey SMS and page.',
  },
  settings: {
    crumbs: ['Workspace', 'Settings'],
    title: 'SMS template',
    lead: 'Customise the survey SMS. Insert variables like the portfolio manager or property address.',
  },
  playground: {
    crumbs: ['Workspace', 'AI playground'],
    title: 'AI playground',
    lead: 'Test Aimee live — flagged sessions never touch your dashboards.',
  },
  team: {
    crumbs: ['Workspace', 'Team'],
    title: 'Staff access',
    lead: 'Invite staff and manage their role. Everyone signs in with Microsoft — there are no passwords to set up.',
  },
};

// The AI playground is a test tool; the backend blocks its mint endpoint in
// prod, so the nav entry only appears outside prod.
const APP_ENV = import.meta.env['VITE_APP_ENV'] ?? 'dev';

const PRIMARY_ITEMS: SidebarItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'home' },
  { id: 'responses', label: 'Responses', icon: 'inbox' },
  { id: 'detractors', label: 'Detractor alerts', icon: 'alert' },
  { id: 'contacts', label: 'Contacts', icon: 'users' },
  { id: 'properties', label: 'Properties', icon: 'building' },
  { id: 'compose', label: 'Send survey', icon: 'send' },
  { id: 'drip', label: 'Drip schedule', icon: 'calendar' },
];

// User (staff-RBAC, 2026-08-10) is read-only — sending/scheduling isn't theirs
// to do, so hide the actions rather than show a button that 403s.
function primaryItems(canWrite: boolean): SidebarItem[] {
  if (canWrite) return PRIMARY_ITEMS;
  return PRIMARY_ITEMS.filter((item) => item.id !== 'compose' && item.id !== 'drip');
}

// No 'reports' entry: Reports was merged into the Dashboard (2026-08-04).
// 'surveys' replaced the General-only 'questions' entry — the question editor is
// now reached by picking a survey first. It's Admin/Manager territory
// (RequireManager guards the routes and the API returns 403 for User), so a
// read-only User doesn't see the entry. 'team' (staff/user management) stays
// ADMIN-only even for Manager.
function secondaryItems(canWrite: boolean, isAdmin: boolean): SidebarItem[] {
  return [
    ...(canWrite ? [{ id: 'surveys', label: 'Surveys', icon: 'copy' } as SidebarItem] : []),
    { id: 'portfolios', label: 'Portfolios', icon: 'briefcase' },
    ...(APP_ENV !== 'prod'
      ? [{ id: 'playground', label: 'AI playground', icon: 'message' } as SidebarItem]
      : []),
    { id: 'settings', label: 'Settings', icon: 'cog' },
    ...(isAdmin ? [{ id: 'team', label: 'Team', icon: 'user' } as SidebarItem] : []),
  ];
}

// Must match DashboardScreen's window so the ['dashboard', N] query cache is shared.
const RESPONSES_WINDOW_DAYS = 30;

// The dashboard greeting was a hardcoded 'Good morning, Kelly.' left over from
// the design mock, so every staff member landed on someone else's name straight
// after login (Kelly's report, 2026-08-20). First name only — it reads as a
// greeting rather than a form field.
function dashboardGreeting(name: string | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first ? `Good morning, ${first}.` : 'Good morning.';
}

// A USER only ever sees their own portfolio — the API pins pm_id to it and
// ignores whatever the client sends (_scope_report_filters, staff-RBAC) — so
// the dashboard hides the manager picker and the lead must not point at it.
const SCOPED_DASHBOARD_LEAD =
  'Results for your portfolio. Filter by date, then export to CSV for Excel.';

// Routes behind _require_write_scope on the API — ADMIN/MANAGER only. A USER
// following any of these gets a 403 on submit, so the button that leads there is
// hidden rather than left to fail on click. '/contacts/new' was the one that
// slipped through: create_contact is write-scoped like the rest.
const WRITE_ROUTES = new Set(['/compose', '/drip', '/contacts/new']);

const ROLE_DISPLAY_LABEL: Record<string, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'Manager',
  USER: 'User',
  VIEWER: 'Viewer',
};

// Detail pages share their list's meta (title, actions) but get a last crumb of
// their own, so the list's crumb above them links back.
const DETAIL_CRUMB: Array<[RegExp, string]> = [
  [/^\/contacts\/(?!new$)[^/]+$/, 'Contact details'],
  [/^\/properties\/[^/]+$/, 'Property details'],
];

function activeKeyFromPath(pathname: string): string {
  if (pathname === '/' || pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/responses/')) return 'detail';
  if (pathname.startsWith('/responses')) return 'responses';
  if (pathname.startsWith('/detractors')) return 'detractors';
  if (pathname.startsWith('/contacts/new')) return 'contacts-new';
  if (/^\/contacts\/[^/]+\/edit/.test(pathname)) return 'contacts-edit';
  if (pathname.startsWith('/contacts')) return 'contacts';
  if (pathname.startsWith('/properties')) return 'properties';
  if (pathname.startsWith('/compose')) return 'compose';
  if (pathname.startsWith('/drip')) return 'drip';
  // /reports redirects to /dashboard; keep it highlighting Dashboard in transit.
  if (pathname.startsWith('/reports')) return 'dashboard';
  // Sub-route first — the chain is order-sensitive.
  if (/^\/surveys\/[^/]+\/questions/.test(pathname)) return 'survey-questions';
  if (pathname.startsWith('/surveys')) return 'surveys';
  // /questions redirects to /surveys; keep it highlighting Surveys in transit.
  if (pathname.startsWith('/questions')) return 'surveys';
  if (pathname.startsWith('/portfolios')) return 'portfolios';
  if (pathname.startsWith('/playground')) return 'playground';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/team')) return 'team';
  return 'dashboard';
}

export function AppShell(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, logout } = useAuth();
  const activeKey = activeKeyFromPath(location.pathname);

  // Below desktop width (tablets, split-screen, phones) the 240px sidebar left
  // too little room for content, so it becomes a drawer behind a menu button.
  const useDrawer = useBreakpoint() !== 'desktop';
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, useDrawer]);

  // Badge reflects open (unresolved) detractors only. Shares the 'detractors'
  // query cache, so marking one resolved updates the count immediately.
  const { data: detractors } = useQuery({
    queryKey: ['detractors'],
    queryFn: () => api.admin.detractors(),
    staleTime: 30_000,
  });
  const openDetractors = (detractors ?? []).filter((d) => d.resolved_at === null).length;
  const canWrite = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const isAdmin = user?.role === 'ADMIN';
  // Mirrors the dashboard's own gate on the manager picker.
  const isScopedUser = user?.role === 'USER';
  const visiblePrimaryItems = primaryItems(canWrite).map((item) =>
    item.id === 'detractors' && openDetractors > 0 ? { ...item, count: openDetractors } : item,
  );
  // Detail / sub-screens highlight their parent item in the sidebar.
  const sidebarActiveId =
    activeKey === 'detail'
      ? 'responses'
      : activeKey === 'contacts-new' || activeKey === 'contacts-edit'
        ? 'contacts'
        : activeKey === 'survey-questions'
          ? 'surveys'
          : activeKey;
  // Responses screen: show the real count of responses captured in the window.
  // Shares the ['dashboard', 30] cache with the Dashboard screen; only fetches
  // while the Responses screen is active.
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard', RESPONSES_WINDOW_DAYS],
    queryFn: () => api.admin.dashboard(RESPONSES_WINDOW_DAYS),
    staleTime: 60_000,
    enabled: activeKey === 'responses',
  });

  // The question editor's crumb names the survey. Same key and fetch as
  // QuestionsScreen (questionsQueryKey), so this reads its cache, not a second call.
  const questionsSurveyId = /^\/surveys\/([^/]+)\/questions/.exec(location.pathname)?.[1];
  const { data: surveyQuestions } = useQuery({
    queryKey: ['survey-questions', questionsSurveyId ?? ''],
    queryFn: () => api.admin.surveyQuestions(questionsSurveyId!),
    staleTime: 30_000,
    enabled: questionsSurveyId !== undefined,
  });

  // Router-relative paths → real hrefs (the app is served under /admin), so a
  // crumb is a genuine link: it opens in a new tab and shows its URL on hover.
  const baseHref = useHref('/').replace(/\/$/, '');

  let meta = SCREEN_META[activeKey] ?? SCREEN_META.dashboard!;
  if (activeKey === 'dashboard') {
    meta = {
      ...meta,
      title: dashboardGreeting(user?.name),
      ...(isScopedUser ? { lead: SCOPED_DASHBOARD_LEAD } : {}),
    };
  }
  if (activeKey === 'responses' && dashboard) {
    const captured = dashboard.kpis.completed;
    const lead = `${captured} ${captured === 1 ? 'response' : 'responses'} captured in the last ${dashboard.window_days} days.`;
    meta = { ...meta, lead };
  }

  let metaCrumbs = meta.crumbs;
  if (activeKey === 'survey-questions' && surveyQuestions?.survey_name) {
    metaCrumbs = [...metaCrumbs.slice(0, -1), surveyQuestions.survey_name];
  }
  const detailCrumb = DETAIL_CRUMB.find(([re]) => re.test(location.pathname))?.[1];
  if (detailCrumb) metaCrumbs = [...metaCrumbs, detailCrumb];
  const crumbs: Crumb[] = metaCrumbs.map((c) =>
    typeof c === 'string'
      ? { label: c }
      : { label: c.label, href: `${baseHref}${c.to}`, onNavigate: () => navigate(c.to) },
  );

  const handleNavigate = (id: string) => {
    if (id === 'dashboard') navigate('/');
    else navigate(`/${id}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // A read-only User has no business sending/scheduling/creating — same gate as
  // the sidebar's compose/drip entries, applied to the top-bar action buttons
  // too. Actions with no `to` (e.g. the detractors Export) are reads and stay.
  const visibleActions = (meta.actions ?? []).filter(
    (a) => canWrite || !a.to || !WRITE_ROUTES.has(a.to),
  );

  const actions = (
    <>
      {visibleActions.map((a) => (
        <Button
          key={a.label}
          variant={a.variant}
          size="sm"
          onClick={() => (a.to ? navigate(a.to) : undefined)}
        >
          {a.label}
        </Button>
      ))}
      <Button variant="tertiary" size="sm" onClick={handleLogout}>
        Sign out
      </Button>
    </>
  );

  const initials = user
    ? user.name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';
  // RequireAuth never renders the shell with a null user, so the fallback is
  // belt-and-braces — but it used to read 'Kelly Stevens / Operations Lead',
  // which is exactly the kind of placeholder that reaches a customer.
  const sidebarUser = user
    ? { initials, name: user.name, role: ROLE_DISPLAY_LABEL[user.role] ?? user.role }
    : { initials: '', name: '', role: '' };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'var(--gc-cream)',
      }}
    >
      <Sidebar
        activeId={sidebarActiveId}
        primaryItems={visiblePrimaryItems}
        secondaryItems={secondaryItems(canWrite, isAdmin)}
        user={sidebarUser}
        brand={{ title: 'Grow&Co', subtitle: 'Survey', logoSrc: logoInline }}
        onNavigate={handleNavigate}
        variant={useDrawer ? 'drawer' : 'static'}
        open={menuOpen}
        onClose={closeMenu}
      />
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          breadcrumbs={crumbs}
          title={meta.title}
          {...(meta.lead !== undefined ? { lead: meta.lead } : {})}
          actions={actions}
          {...(useDrawer ? { onMenuClick: () => setMenuOpen(true), menuOpen } : {})}
        />
        <Outlet />
      </main>
    </div>
  );
}
